import ExpandDownIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  ListItemText,
  Menu,
  MenuItem,
  Button as MuiButton,
  ButtonGroup as MuiButtonGroup,
  useTheme,
} from '@mui/material';
import React, { useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from 'src/components/atoms';
import {
  AuthorisedCaseImporter,
  AuthorisedNewReportCreation,
  AuthorizedExistReportCreation,
} from 'src/components/auth';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { useIsFeatureEnabled } from 'src/hooks';
import * as Types from 'src/types';
import { FeatureFlag, State } from 'src/types';
import { trackCaseImportEnteredEvent } from 'src/utils/analytics/track/caseImport';
import { FormInput } from '../FormInput';

enum CaseCreationOptions {
  NEW_CASE = 'Add a new case',
  HISTORICAL_CASE = 'Add an existing case',
  BULK_IMPORT = 'Add multiple cases',
}

interface CaseCreateNewButtonProps {
  addNewReport: () => void;
  addExistingReport: () => void;
}

export const CaseCreateNewButton = ({
  addNewReport,
  addExistingReport,
}: CaseCreateNewButtonProps) => {
  const [isButtonLoading, setIsButtonLoading] = React.useState(false);
  const history = useHistory();
  const isAuthorizedExistReportCreation = AuthorizedExistReportCreation();
  const isAuthorisedNewReportCreation = AuthorisedNewReportCreation();
  const isAuthorizedBulkImport = AuthorisedCaseImporter();
  const caseImporterEnabled = useIsFeatureEnabled(FeatureFlag.CASE_IMPORTER);
  const segmentEnabled = useIsFeatureEnabled(
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const isLegacyMode = useSelector(
    (state: State) => state.organisation?.isLegacyMode,
  );

  const { presets } = useTheme<LensTheme>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const presetsAny = presets as any;
  const SplitButtonPresets = presetsAny?.SplitButtonPresets ?? {};
  const bottomRight =
    presetsAny?.MenuPresets?.positionFromAnchorEl?.bottomRight ?? {};

  const state = useSelector(
    (state: Types.State) => ({
      organisationId: state.organisation.organisation?.id || null,
      userId: state.globalUsers.currentUser?.id || null,
      userRole:
        (state.globalUsers.currentUser?.SystemRoles ?? [])
          .map((role) => role.name)
          .join(', ') || null,
    }),
    shallowEqual,
  );

  const buttonsOptions: {
    label: CaseCreationOptions;
    value: string;
    'data-testid': string;
  }[] = [];
  if (isAuthorisedNewReportCreation) {
    buttonsOptions.push({
      label: CaseCreationOptions.NEW_CASE,
      value: 'new_case',
      'data-testid': 'add-case',
    });
  }
  if (isAuthorizedExistReportCreation) {
    buttonsOptions.push({
      label: CaseCreationOptions.HISTORICAL_CASE,
      value: 'historical_case',
      'data-testid': 'add-existing-case',
    });
  }
  if (isAuthorizedBulkImport && caseImporterEnabled) {
    buttonsOptions.push({
      label: CaseCreationOptions.BULK_IMPORT,
      value: 'bulk_import',
      'data-testid': 'add-multiple-cases',
    });
  }

  const handleOnChange = (value: string) => {
    setIsButtonLoading(true);
    switch (value) {
      case CaseCreationOptions.NEW_CASE:
        addNewReport();
        return;
      case CaseCreationOptions.HISTORICAL_CASE:
        addExistingReport();
        return;
      case CaseCreationOptions.BULK_IMPORT:
        if (segmentEnabled && state.organisationId) {
          trackCaseImportEnteredEvent({
            organisation_id: Number(state.organisationId),
            user_id: String(state.userId),
            user_system_role: state.userRole ?? null,
            entry_point: 'cases_add_multiple',
          });
        }
        history.push('/uploadCases');
        return;
    }
  };

  if (!isLegacyMode && buttonsOptions.length > 0) {
    return (
      <>
        <MuiButtonGroup
          ref={anchorRef}
          aria-label="Add a case"
          data-testid="add-new-case-container-button"
          {...SplitButtonPresets}
        >
          <MuiButton
            variant="contained"
            data-testid="add-new-case-main-button"
            disabled={isButtonLoading}
            onClick={() => {
              buttonsOptions[0] && handleOnChange(buttonsOptions[0].label);
              setMenuOpen(false);
            }}
            startIcon={<i className="uil uil-plus" />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {buttonsOptions[0]?.label || 'Add new case'}
          </MuiButton>
          {buttonsOptions.length > 1 && (
            <MuiButton
              variant="contained"
              id="add-new-case-more-options"
              aria-label="More options"
              aria-controls={
                menuOpen ? 'add-new-case-dropdown-menu' : undefined
              }
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              data-testid="add-new-case-more-options"
              onClick={() => setMenuOpen(true)}
            >
              <ExpandDownIcon aria-hidden />
            </MuiButton>
          )}
        </MuiButtonGroup>
        {buttonsOptions.length > 1 && (
          <Menu
            id="add-new-case-dropdown-menu"
            open={menuOpen}
            anchorEl={anchorRef.current}
            onClose={() => setMenuOpen(false)}
            data-testid="add-new-case-dropdown-menu"
            slotProps={{
              list: { 'aria-labelledby': 'add-new-case-more-options' },
              paper: {
                sx: { minWidth: anchorRef.current?.offsetWidth ?? 'auto' },
              },
            }}
            {...bottomRight}
          >
            {buttonsOptions.map((option) => (
              <MenuItem
                key={option.value}
                onClick={() => {
                  handleOnChange(option.label);
                  setMenuOpen(false);
                }}
                data-testid={option['data-testid']}
              >
                <ListItemText>{option.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );
  }

  if (buttonsOptions.length === 1) {
    return (
      <Button
        data-testid={buttonsOptions[0]['data-testid']}
        onClick={() => handleOnChange(buttonsOptions[0].label)}
        variant="primary"
        size="medium"
        loading={isButtonLoading}
      >
        <StyledIconInButton
          size="small"
          position="left"
          className="uil uil-plus"
        />
        {buttonsOptions[0].label}
      </Button>
    );
  } else if (buttonsOptions.length > 1) {
    return (
      <FormInput
        variant={Types.FormInputVariant.DROPDOWN}
        data-testid={'add-new-case-dropdown'}
        buttonText={'Add new case'}
        dropdownButtonSize="medium"
        isMulti={false}
        hideDropdownIndicator={false}
        buttonVariant={'primary'}
        options={buttonsOptions}
        onChange={(option) => {
          if (option && !Array.isArray(option)) {
            const selectedOption = option as {
              label: CaseCreationOptions;
              value: string;
            };
            handleOnChange(selectedOption.label);
          }
        }}
        controlShouldRenderValue={false}
        $zIndex={1000}
        withBorder={true}
        $containerWidth="auto"
      />
    );
  }
  return null;
};
